import React, { useState, createContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import { Orders } from './pages/Orders';
import { Users } from './pages/Users';
import { Drivers } from './pages/Drivers';
import { Wallet } from './pages/Wallet';
import { ManualBooking } from './pages/ManualBooking';
import { Reports } from './pages/Reports';
import { Settings } from './pages/Settings';
import { SignIn } from './pages/SignIn';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
// Language context
export const LanguageContext = createContext({
  language: 'en',
  direction: 'ltr',
  setLanguage: (lang: string) => {}
});
// Protected route component
const ProtectedRoute = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const {
    isAuthenticated,
    isLoading
  } = useAuth();
  const location = useLocation();
  if (isLoading) {
    // Show loading state while checking authentication
    return <div className="min-h-screen flex items-center justify-center bg-gray-50" data-id="element-0">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-congress-blue-600" data-id="element-1"></div>
      </div>;
  }
  if (!isAuthenticated) {
    // Redirect to the signin page, but save the current location
    return <Navigate to="/signin" state={{
      from: location
    }} replace data-id="element-2" />;
  }
  return <>{children}</>;
};
// AnimatedRoutes component to handle route transitions
const AnimatedRoutes = () => {
  const location = useLocation();
  return <AnimatePresence mode="wait" data-id="element-3">
      <Routes location={location} key={location.pathname} data-id="element-4">
        <Route path="/signin" element={<SignIn data-id="element-6" />} data-id="element-5" />
        <Route path="/" element={<Navigate to="/dashboard" replace data-id="element-8" />} data-id="element-7" />
        <Route path="/dashboard" element={<ProtectedRoute data-id="element-10">
              <Dashboard data-id="element-11" />
            </ProtectedRoute>} data-id="element-9" />
        <Route path="/orders" element={<ProtectedRoute data-id="element-13">
              <Orders data-id="element-14" />
            </ProtectedRoute>} data-id="element-12" />
        <Route path="/users" element={<ProtectedRoute data-id="element-16">
              <Users data-id="element-17" />
            </ProtectedRoute>} data-id="element-15" />
        <Route path="/drivers" element={<ProtectedRoute data-id="element-19">
              <Drivers data-id="element-20" />
            </ProtectedRoute>} data-id="element-18" />
        <Route path="/wallet" element={<ProtectedRoute data-id="element-22">
              <Wallet data-id="element-23" />
            </ProtectedRoute>} data-id="element-21" />
        <Route path="/manual-booking" element={<ProtectedRoute data-id="element-25">
              <ManualBooking data-id="element-26" />
            </ProtectedRoute>} data-id="element-24" />
        <Route path="/reports" element={<ProtectedRoute data-id="element-28">
              <Reports data-id="element-29" />
            </ProtectedRoute>} data-id="element-27" />
        <Route path="/settings" element={<ProtectedRoute data-id="element-31">
              <Settings data-id="element-32" />
            </ProtectedRoute>} data-id="element-30" />
      </Routes>
    </AnimatePresence>;
};
// AuthenticatedApp component that wraps the main app with Layout
const AuthenticatedApp = () => {
  const {
    pathname
  } = useLocation();
  // Don't apply layout to the sign-in page
  if (pathname === '/signin') {
    return <AnimatedRoutes data-id="element-33" />;
  }
  return <Layout data-id="element-34">
      <AnimatedRoutes data-id="element-35" />
    </Layout>;
};
export function App() {
  const [language, setLanguage] = useState('en');
  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
  };
  const languageContextValue = {
    language,
    direction: language === 'ar' ? 'rtl' : 'ltr',
    setLanguage: handleLanguageChange
  };
  return <LanguageContext.Provider value={languageContextValue} data-id="element-36">
      <Router data-id="element-37">
        <AuthProvider data-id="element-38">
          <NotificationProvider data-id="element-39">
            <div dir={languageContextValue.direction} className={`font-${language === 'ar' ? 'cairo' : 'inter'} min-h-screen bg-gray-50`} data-id="element-40">
              <AuthenticatedApp data-id="element-41" />
            </div>
          </NotificationProvider>
        </AuthProvider>
      </Router>
    </LanguageContext.Provider>;
}